import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
  ContentChild,
  OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  forkJoin,
  last,
  map,
  Observable,
  of,
  switchMap,
  tap,
  firstValueFrom,
} from 'rxjs';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { EntityService } from 'src/app/services/entity.service';
import { UsersService } from 'src/app/services/users.service';
import { CoursesService } from 'src/app/services/courses.service';
import { CourseRequestsService } from 'src/app/services/course-requests.service';
import { VerificationComponent } from '../verification/verification.component';
import { ToastService } from 'src/app/services/toast.service';

export interface PendingFileInfo {
  name: string;
  size: string;
  mimeType?: string;
}
@Component({
  selector: 'app-upload-file',
  templateUrl: './upload-file.component.html',
  styleUrls: ['./upload-file.component.scss'],
})
export class UploadFileComponent implements OnInit, OnChanges {
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ContentChild('overrideFooter', { read: ElementRef }) overrideFooter?: ElementRef;
  @Input() fileName!: string;
  @Input() fillSpace = true;
  @Input() noUploadDropzone = false;
  @Input() fileTypeInS3: 'ENTITY_LOGO' | 'USER_AVATAR' | 'COURSE_DOCUMENT' = 'ENTITY_LOGO';
  @Input() showReplaceButton = true;
  @Input() entityId!: number;
  @Input() Id!: number;
  @Input() showFilesOnly = false;
  @Input() enableWebPConversion = true; // New input to control WebP conversion
  @Input() webPQuality = 0.8; // WebP quality (0.0 to 1.0)
  @Input() courseRequestId?: number;
  @Input() existingDocumentsInput?: { assetId: number; fileName: string; fileMimeType: string; assetUrl?: string }[]; // Allow passing documents directly
  @Input()
  set courseItemId(value: number | string) {
    this._courseItemId = +value;
  }
  get courseItemId(): number {
    return this._courseItemId;
  }
  @Output() replaceImage = new EventEmitter<void>();
  @Output() imageLoaded = new EventEmitter<string>();
  @Output() uploadComplete = new EventEmitter<void>();
  @Output() pendingFilesChange = new EventEmitter<PendingFileInfo[]>();
  @Output() documentDeleted = new EventEmitter<void>();

  maxFileSizeExceeded = false;
  wrongFileType = false;
  isDragging = false;
  isUploading = false;
  uploadCompleted = false;
  attemptedFileName: string | null = null;
  lastUploadedFilename: string | null = null;
  lastUploadedFilePath: string | null = null;
  imageSrc: string | null = null;
  selectedFile: File | null = null;
  selectedFiles: File[] = [];
  progress = 0;
  uploadedFileInfo:
    | {
      name: string;
      size: string;
      icon: string;
      isCompleted?: boolean;
      originalSize?: string;
      compressionRatio?: string;
    }[]
    | null = null;
  existingDocuments: { assetId: number; fileName: string; fileMimeType: string; assetUrl?: string }[] = [];

  private readonly maxFileSize = 10 * 1024 * 1024; // 10 MB
  private _courseItemId!: number;

  /**
   * Sanitizes filename by replacing spaces with underscores
   * @param filename The original filename
   * @returns The sanitized filename
   */
  private sanitizeFilename(filename: string): string {
    return encodeURIComponent(filename);
  }

  decodeFilename(filename: string): string {
    return decodeURIComponent(filename);
  }

  constructor(
    public dialog: MatDialog,
    private entityService: EntityService,
    private UsersService: UsersService,
    private coursesService: CoursesService,
    private courseRequestsService: CourseRequestsService,
    private toastService: ToastService,
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    const f = changes['fileName'];
    if (f && !f.firstChange && this.fileName) {
      const mime = this.getFileIcon(this.fileName);
      this.loadImage(this.fileName, mime);
    }
    
    // If existingDocumentsInput is provided, use it directly and don't fetch from API
    const existingDocsChange = changes['existingDocumentsInput'];
    if (existingDocsChange && this.existingDocumentsInput && this.existingDocumentsInput.length > 0) {
      this.existingDocuments = this.existingDocumentsInput.map((d: any) => ({
        assetId: Number(d.id || d.assetId),
        fileName: d.fileName,
        fileMimeType: d.fileMimeType,
        assetUrl: d.assetUrl,
      }));
      return; // Don't fetch from API if documents were passed directly
    }
    
    // Watch for courseItemId changes and refetch documents ONLY if no documents were passed directly
    const courseItemIdChange = changes['courseItemId'];
    if (courseItemIdChange && !courseItemIdChange.firstChange && this.showFilesOnly && this.courseItemId && (!this.existingDocumentsInput || this.existingDocumentsInput.length === 0)) {
      this.fetchExistingDocuments().subscribe();
    }
  }

  ngOnInit() {
    // If documents are passed directly, use them and don't fetch from API
    if (this.existingDocumentsInput && this.existingDocumentsInput.length > 0) {
      this.existingDocuments = this.existingDocumentsInput.map((d: any) => ({
        assetId: Number(d.id || d.assetId),
        fileName: d.fileName,
        fileMimeType: d.fileMimeType,
        assetUrl: d.assetUrl,
      }));
      // Don't fetch from API if documents are already provided
      return;
    }
    
    // Only fetch from API if no documents were provided and showFilesOnly is true
    if (this.showFilesOnly == true && this.courseItemId) {
      this.fetchExistingDocuments().subscribe();
    }
  }

  downloadDocument(doc: { assetUrl?: string; fileName: string; fileMimeType: string }) {
    if (!doc.assetUrl) {
      this.toastService.setToast({
        text: 'Download not available.',
        type: 'error',
        dismissible: false,
        icon: true,
      });
      return;
    }

    fetch(doc.assetUrl)
      .then((response) => {
        if (!response.ok) throw new Error('Network error');
        return response.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Decode the filename for download to show the original name
        a.download = this.decodeFilename(doc.fileName);
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        this.toastService.setToast({
          text: 'Failed to download file.',
          type: 'error',
          dismissible: false,
          icon: true,
        });
      });
  }

  deleteDocument(doc: { assetId: number; assetUrl?: string; fileName: string; fileMimeType: string }) {
    // For course documents, we only need courseItemId (entityId check not needed)
    if (this.fileTypeInS3 === 'COURSE_DOCUMENT') {
      if (!this.courseItemId) {
        console.error('Cannot delete document: courseItemId is required');
        return;
      }

      // Show confirmation dialog before deleting
      const confirmDialogRef = this.dialog.open(VerificationComponent, {
        data: {
          type: 'alert',
          title: 'Delete Document',
          text: `Are you sure you want to delete "${this.decodeFilename(doc.fileName)}"? This action cannot be undone.`,
          confirmButtonTitle: 'Yes, delete',
          cancelButtonTitle: 'No, cancel',
          confirmButtonClass: 'red',
        },
      });

      confirmDialogRef.afterClosed().subscribe((result) => {
        if (result === 'verified') {
          this.coursesService.removeCourseDocument(this.courseItemId, doc.assetId).subscribe({
            next: () => {
              // Always emit event for parent to refresh (for course requests)
              this.documentDeleted.emit();
              
              // Also refresh local documents list
              this.fetchExistingDocuments().subscribe();
            },
            error: (err) => {
              console.error('Failed to delete course document', err);
              const errorMessage = err?.error?.message || err?.message || 'Failed to delete document. Please try again.';
              this.toastService.setToast({
                text: errorMessage,
                type: 'error',
                dismissible: false,
                icon: true,
              });
            },
          });
        }
      });
      return;
    }

    // For other file types (ENTITY_LOGO, USER_AVATAR), check entityId
    const entityData = this.entityService.getEntityDataValue();
    if (!entityData || !entityData.id) {
      console.error('Cannot delete document: entityId is required');
      return;
    }

    // Handle deletion for other file types if needed
    // (This would be for entity logos or user avatars)
  }

  public fetchExistingDocuments(): Observable<any> {
    if (!this.courseItemId) {
      this.existingDocuments = [];
      return of([]);
    }
    return this.coursesService
      .getCourseSession2(this.courseItemId, { includedAssociations: 'Documents,Course' })
      .pipe(map((resp: any) => {
        const docs = resp.data?.Documents || [];
        this.existingDocuments = docs.map((d: any) => ({
          assetId: d.id,
          fileName: d.fileName,
          fileMimeType: d.fileMimeType,
          assetUrl: d.assetUrl, // if you want to link/view
        }));
        return this.existingDocuments;
      }));
  }

  private shouldConvertToWebP(file: File): boolean {
    if (!this.enableWebPConversion) return false;

    const imageTypes = [
      'image/jpeg', 'image/jpg', 'image/png',
    ];
    return imageTypes.includes(file.type.toLowerCase());
  }

  private convertToWebP(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;

        ctx?.drawImage(
          img, 0, 0,
        );

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const originalName = file.name.substring(0, file.name.lastIndexOf('.'));
              const sanitizedOriginalName = this.sanitizeFilename(originalName);
              const webpFile = new File(
                [blob], `${sanitizedOriginalName}.webp`, {
                type: 'image/webp',
                lastModified: Date.now(),
              },
              );
              resolve(webpFile);
            } else {
              reject(new Error('Failed to convert image to WebP'));
            }
          },
          'image/webp',
          this.webPQuality,
        );
      };

      img.onerror = () => {
        reject(new Error('Failed to load image for WebP conversion'));
      };

      img.src = URL.createObjectURL(file);
    });
  }

  private async processFilesWithConversion(files: File[]): Promise<File[]> {
    const processedFiles: File[] = [];

    for (const file of files) {
      try {
        if (this.shouldConvertToWebP(file)) {
          console.log(`Converting ${file.name} to WebP...`);
          const webpFile = await this.convertToWebP(file);
          processedFiles.push(webpFile);
        } else {
          processedFiles.push(file);
        }
      } catch (error) {
        console.error(`Failed to convert ${file.name} to WebP:`, error);
        processedFiles.push(file);
      }
    }

    return processedFiles;
  }

  get hasOverrideFooter(): boolean {
    return !!this.overrideFooter;
  }

  onReplace() {
    this.noUploadDropzone = false;
    this.replaceImage.emit();
  }

  private validateFiles(files: File[]): File[] {
    const validFiles: File[] = [];

    for (const file of files) {
      this.attemptedFileName = file.name;

      if (!this.isValidFileType(file)) {
        this.wrongFileType = true;
        continue;
      }

      if (file.size > this.maxFileSize) {
        this.maxFileSizeExceeded = true;
        continue;
      }

      validFiles.push(file);
    }

    return validFiles;
  }

  private createFileUploadInfo(file: File, originalFile: File) {
    const wasConverted = file.name !== originalFile.name;

    return {
      name: file.name,
      size: this.formatFileSize(file.size),
      icon: this.getFileIcon(file.name),
      isCompleted: false,
      originalSize: wasConverted ? this.formatFileSize(originalFile.size) : undefined,
      compressionRatio: wasConverted ? this.calculateCompressionRatio(originalFile.size, file.size) : undefined,
    };
  }

  async processNewFile(newFiles: File[]) {
    if (!newFiles || newFiles.length === 0) {
      return;
    }

    const prevFiles = this.selectedFiles;

    this.resetUploadState();
    const validFiles = this.validateFiles(newFiles);

    if (validFiles.length === 0 && (this.wrongFileType || this.maxFileSizeExceeded)) {
      return;
    }

    const processedFiles = await this.processFilesWithConversion(validFiles);

    // Check for duplicates if this is a course document upload
    // We need to check against original filenames (before WebP conversion) to catch true duplicates
    if (this.fileTypeInS3 === 'COURSE_DOCUMENT') {
      const duplicateFiles = await this.checkForDuplicateFiles(validFiles, prevFiles);
      
      if (duplicateFiles.length > 0) {
        // Show warning dialog about duplicates
        const duplicateCount = duplicateFiles.length;
        const fileText = duplicateCount === 1 ? 'file' : 'files';
        const fileNames = duplicateFiles.join(', ');
        const message = duplicateCount === 1
          ? `A file named "${fileNames}" is already selected. Please choose a different file.`
          : `The following ${fileText} are already selected: ${fileNames}. Please choose different files.`;

        this.dialog.open(VerificationComponent, {
          data: {
            type: 'alert',
            title: 'Duplicate File' + (duplicateCount > 1 ? 's' : ''),
            text: message,
            confirmButtonTitle: 'OK',
            confirmButtonClass: 'green',
          },
        });
        
        // Filter out duplicate files by comparing original filenames
        // We need to match processedFiles with validFiles by index to get original names
        const uniqueProcessedFiles: File[] = [];
        const uniqueValidFiles: File[] = [];
        
        validFiles.forEach((originalFile: File, index: number) => {
          const originalFileName = originalFile.name.toLowerCase().trim();
          const isDuplicate = duplicateFiles.some((dupName: string) => 
            dupName.toLowerCase().trim() === originalFileName
          );
          
          if (!isDuplicate) {
            uniqueValidFiles.push(originalFile);
            uniqueProcessedFiles.push(processedFiles[index]);
          }
        });
        
        if (uniqueProcessedFiles.length === 0) {
          // All files were duplicates, don't add anything
          return;
        }
        
        // Use only unique files
        this.selectedFiles = [...prevFiles, ...uniqueProcessedFiles];
        
        // Create upload info only for unique files
        const newUploadInfo = uniqueProcessedFiles.map((file, index) => {
          const originalFile = uniqueValidFiles[index];
          return this.createFileUploadInfo(file, originalFile);
        });
        this.uploadedFileInfo = [...(this.uploadedFileInfo ?? []), ...newUploadInfo];
      } else {
        // No duplicates, add all files
        this.selectedFiles = [...prevFiles, ...processedFiles];
        const newUploadInfo = processedFiles.map((file, index) => {
          const originalFile = validFiles[index];
          return this.createFileUploadInfo(file, originalFile);
        });
        this.uploadedFileInfo = [...(this.uploadedFileInfo ?? []), ...newUploadInfo];
      }
    } else {
      // For single file uploads (avatars/logos), start upload immediately
      this.selectedFiles = processedFiles;
      const uploadInfo = this.createFileUploadInfo(this.selectedFiles[0], validFiles[0]);
      this.uploadedFileInfo = [uploadInfo];
    }
    this.emitPendingFiles();
  }

  private async checkForDuplicateFiles(newFiles: File[], existingFiles: File[]): Promise<string[]> {
    const duplicateNames: string[] = [];
    
    // Only check against already selected files (prevFiles)
    // We do NOT check against existing documents on the server here because:
    // 1. For new course requests, there are no existing documents yet
    // 2. For existing course requests, the upload-course-documents component handles
    //    duplicate checking against server documents and allows replacement
    // Note: newFiles are the original files (before WebP conversion)
    // existingFiles are the processed files (may have been converted to WebP)
    // We need to compare original filenames, so we'll extract original names from processed files
    newFiles.forEach((newFile: File) => {
      const newFileName = newFile.name.toLowerCase().trim();
      
      const isDuplicate = existingFiles.some((existingFile: File) => {
        // For existing files that were converted to WebP, we need to extract the original name
        // The processed file name format is: encoded(originalName).webp
        let existingFileName = existingFile.name.toLowerCase().trim();
        
        // If the existing file is a WebP file, try to extract the original name
        if (existingFile.name.endsWith('.webp')) {
          try {
            // Decode the filename (it's URL encoded)
            const decoded = decodeURIComponent(existingFile.name);
            // Remove .webp extension to get back to original name
            if (decoded.endsWith('.webp')) {
              existingFileName = decoded.substring(0, decoded.length - 5).toLowerCase().trim();
            }
          } catch (e) {
            // If decoding fails, try without decoding
            existingFileName = existingFile.name.substring(0, existingFile.name.length - 5).toLowerCase().trim();
          }
        }
        
        return existingFileName === newFileName;
      });
      
      if (isDuplicate) {
        duplicateNames.push(newFile.name);
      }
    });
    
    return duplicateNames;
  }

  async onFileSelected(event: any): Promise<void> {
    const files: FileList = event.target.files;
    await this.processNewFile(Array.from(files));

    event.target.value = '';
  }

  private calculateCompressionRatio(originalSize: number, compressedSize: number): string {
    const ratio = ((originalSize - compressedSize) / originalSize) * 100;
    return `${Math.round(ratio)}%`;
  }

  private resetUploadState(): void {
    this.uploadCompleted = false;
    this.progress = 0;
    this.isUploading = false;
    this.maxFileSizeExceeded = false;
    this.wrongFileType = false;
  }

  removeFile(event?: MouseEvent, idx?: number): void {
    if (event) event.stopPropagation();

    if (typeof idx === 'number') {
      const removedFile = this.selectedFiles[idx];
      const removedInfo = this.uploadedFileInfo?.[idx];

      this.selectedFiles.splice(idx, 1);
      if (this.uploadedFileInfo) {
        this.uploadedFileInfo.splice(idx, 1);
        if (this.uploadedFileInfo.length === 0) {
          this.uploadedFileInfo = null;
        }
      }

    } else {
      this.selectedFiles = [];
      this.uploadedFileInfo = null;
    }
    this.emitPendingFiles();
  }

  onTryAgain(event: MouseEvent): void {
    event.stopPropagation();
    // reset everything back to the start
    this.maxFileSizeExceeded = false;
    this.wrongFileType = false;
    this.removeFile(event);
  }

  private imageToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        resolve(base64String);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  }

  private base64ToBlob(base64: string, mime: string): Blob {
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mime });
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = [
      'Bytes', 'KB', 'MB',
    ];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  public getFileIcon(filename: string): string {
    const extension = filename.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'picture_as_pdf';
      case 'doc':
      case 'docx':
        return 'article';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'webp':
        return 'image';
      case 'xls':
        return 'dataset';
      default:
        return 'file_present';
    }
  }

  async onFileDropped(event: DragEvent): Promise<void> {
    event.preventDefault();
    this.isDragging = false;
    const files = event.dataTransfer?.files;

    await this.processNewFile(Array.from(files));
  }

  public processFile(file: File): void {
    this.maxFileSizeExceeded = false;
    this.wrongFileType = false;
    this.attemptedFileName = file.name;

    if (!this.isValidFileType(file)) {
      console.error('Invalid file type:', file.type);
      return;
    }

    if (file.size > this.maxFileSize) {
      console.error(`File size exceeds ${this.maxFileSize} bytes (10 MB)`);
      this.maxFileSizeExceeded = true;
      return;
    }
  }

  public isValidFileType(file: File): boolean {
    if (this.fileTypeInS3 === 'ENTITY_LOGO') {
      const allowedMimeTypes = [
        'image/jpeg', 'image/png', 'image/jpg', 'image/webp',
      ];
      const isValid = allowedMimeTypes.includes(file.type);
      this.wrongFileType = !isValid;
      return isValid;
    }

    if (this.fileTypeInS3 === 'USER_AVATAR') {
      const allowedMimeTypes = [
        'image/jpeg', 'image/png', 'image/jpg', 'image/webp',
      ];
      const isValid = allowedMimeTypes.includes(file.type);
      this.wrongFileType = !isValid;
      return isValid;
    }

    if (this.fileTypeInS3 === 'COURSE_DOCUMENT') {
      const allowedMimeTypes = [
        'image/jpeg',
        'image/png',
        'image/webp',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
      ];
      const isValid = allowedMimeTypes.includes(file.type);
      this.wrongFileType = !isValid;
      return isValid;
    }

    const allowedMimeTypes = [
      'image/jpeg',
      'image/png',
      'image/webp',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
    ];
    const isValid = allowedMimeTypes.includes(file.type);
    this.wrongFileType = !isValid;
    return isValid;
  }

  async handleFile(originalFile: File) {
    // Convert to WebP if applicable
    let processedFile = originalFile;
    if (this.shouldConvertToWebP(originalFile)) {
      try {
        processedFile = await this.convertToWebP(originalFile);
        console.log(`Converted ${originalFile.name} to WebP: ${processedFile.name}`);
      } catch (error) {
        console.error('WebP conversion failed, using original file:', error);
      }
    }

    if (!this.isValidFileType(processedFile)) {
      console.error('Invalid file type:', processedFile.type);
      return;
    }
    if (processedFile.size > this.maxFileSize) {
      console.error(`File size exceeds ${this.maxFileSize} bytes (10 MB)`);
      return;
    }

    const uploadInfo = this.createFileUploadInfo(processedFile, originalFile);
    this.uploadedFileInfo.push(uploadInfo);

    try {
      const dataUrl = await this.imageToBase64(processedFile);
      const rawBase64 = dataUrl.replace(/^data:image\/\w+;base64,/, '');
      const blob = this.base64ToBlob(rawBase64, processedFile.type);
      const sanitizedFileName = this.sanitizeFilename(processedFile.name);
      this.entityService.generateSignedUrl(
        sanitizedFileName, processedFile.type, this.fileTypeInS3,
      ).subscribe({
        next: (response: any) => {
          const uploadUrl = response.data.uploadUrl;
          this.entityService.uploadSignedUrl(uploadUrl, blob).subscribe({
            next: () => { },
            error: (uploadError) => {
              console.error('Upload failed:', uploadError);
            },
          });
        },
        error: (error) => {
          console.error('Error getting signed URL:', error);
        },
      });
    } catch (error) {
      console.error('Error converting file:', error);
    }
  }


  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
  }

  // #region Upload File

  changeAvatar(file) {
    const userId = this.entityService.user.id;
    if (this.entityId === undefined || this.entityId === null) return;
    const sanitizedFileName = this.sanitizeFilename(file.name);
    const filePath = `entities/${this.entityId}/avatars/${userId}-${sanitizedFileName}`;
    this.UsersService.setUserLogo(
      this.entityService.user.loginName, filePath, sanitizedFileName,
    ).subscribe({
      next: () => {
        this.entityService.getEntityDataValue();
        this.UsersService.triggerLogoReload();
        this.uploadComplete.emit();
      },
      error: (err) => {
        console.error('Failed to save user avatar metadata', err);
      },
    });
  }

  changeEntityLogo(file) {
    const entityData = this.entityService.getEntityDataValue();
    const entityId = entityData?.id;
    if (!entityId) return;
    const sanitizedFileName = this.sanitizeFilename(file.name);
    const filePath = `entities/${entityId}/assets/entityLogo.${sanitizedFileName}`;
    this.entityService.setEntityLogo(
      this.entityService.user.loginName, filePath, sanitizedFileName,
    ).subscribe({
      next: () => {
        this.entityService.getEntityDataValue();
        this.entityService.triggerLogoReload();
        this.uploadComplete.emit();
      },
      error: (err) => {
        console.error('Failed to save entity logo metadata', err);
      },
    });
  }

uploadCourseDocuments(files: File[]) {
  const entityId =
    this.entityId ??
    this.entityService.getEntityDataValue()?.id;

  const courseItemIdNum = Number(this.courseItemId);

  const documentsArray = files.map((file) => {
    const sanitizedFileName = this.sanitizeFilename(file.name);
    const filePath =
      `entities/${entityId}/courseDocuments/${courseItemIdNum}/${sanitizedFileName}`;

    return {
      fileName: file.name,
      filePath,
      fileMimeType: file.type,
    };
  });

  this.coursesService
    .createCourseDocument(
      this.entityService.user.loginName,
      documentsArray,
      courseItemIdNum,
    )
    .subscribe({
      next: () => {
        this.uploadComplete.emit();
        this.isUploading = false;
      },
      error: (err) => {
        console.error(
          '[uploadCourseDocuments] Failed to save course document metadata',
          err,
        );
        this.isUploading = false;
        const errorMessage = err?.error?.message || err?.message || 'Failed to upload document. Please try again.';
        this.toastService.setToast({
          text: errorMessage,
          type: 'error',
          dismissible: false,
          icon: true,
        });
        // Still emit uploadComplete to stop spinner, but the error is logged
        this.uploadComplete.emit();
      },
    });
}


startUpload(event: Event) {
  event.stopPropagation();
  if (!this.selectedFiles.length || this.isUploading) return;
  this.isUploading = true;

  // Multi-file upload logic:
  const uploads: Observable<any>[] = this.selectedFiles.map((file) =>
    this.uploadFile(file).pipe(
      tap((event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          this.progress = Math.round((100 * event.loaded) / (event.total || 1));
        }
      }),
      last()
    )
  );

  forkJoin(uploads).subscribe({
    next: () => {
      this.uploadCompleted = true;

      if (this.uploadedFileInfo) {
        this.uploadedFileInfo.forEach((fileInfo) => {
          fileInfo.isCompleted = true;
        });
      }

      if (this.fileTypeInS3 === 'COURSE_DOCUMENT') {
        this.uploadCourseDocuments(this.selectedFiles);
      } else if (this.fileTypeInS3 === 'ENTITY_LOGO') {
        this.changeEntityLogo(this.selectedFiles[0]);
      } else if (this.fileTypeInS3 === 'USER_AVATAR') {
        this.changeAvatar(this.selectedFiles[0]);
      }
      
      this.isUploading = false;
      this.clearUploadedFiles();
    },
    error: (err) => {
      this.isUploading = false;
      console.error('Upload failed:', err);
      const errorMessage = err?.error?.message || err?.message || 'Failed to upload file. Please try again.';
      this.toastService.setToast({
        text: errorMessage,
        type: 'error',
        dismissible: false,
        icon: true,
      });
      // Emit uploadComplete even on error to stop spinner
      this.uploadComplete.emit();
    },
  });
}

    startUploadProgrammatically() {
    this.startUpload(new MouseEvent('click'));
  }

  private clearUploadedFiles(): void {
    this.selectedFiles = [];
    this.uploadedFileInfo = null;

    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private uploadFile(file: File): Observable<any> {
    if (this.fileTypeInS3 === 'COURSE_DOCUMENT') {
      const sanitizedFileName = this.sanitizeFilename(file.name);
      return this.entityService.generateSignedUrl(
        sanitizedFileName, file.type, this.fileTypeInS3, this.courseItemId,
      ).pipe(switchMap((response: any) => {
        const uploadUrl = response.data.uploadUrl;
        return this.entityService.uploadSignedUrl(uploadUrl, file).pipe(tap((event: HttpEvent<any>) => {
          if (event.type === HttpEventType.UploadProgress) {
            this.progress = Math.round((100 * event.loaded) / (event.total || 1));
          }
        }),
          last());
      }));
    }

    const sanitizedFileName = this.sanitizeFilename(file.name);
    return this.entityService.generateSignedUrl(
      sanitizedFileName, file.type, this.fileTypeInS3,
    ).pipe(switchMap((response: any) => {
      const uploadUrl = response.data.uploadUrl;
      return this.entityService.uploadSignedUrl(uploadUrl, file).pipe(tap((event: HttpEvent<any>) => {
        if (event.type === HttpEventType.UploadProgress) {
          this.progress = Math.round((100 * event.loaded) / (event.total || 1));
        }
      }),
        last());
    }));
  }

  loadImage(fileName: string, fileType: string) {
    if (this.imageSrc) {
      URL.revokeObjectURL(this.imageSrc);
    }

    this.entityService
      .generateSignedUrl(
        fileName, fileType, this.fileTypeInS3,
      )
      .pipe(map((resp: any) => resp.data.getUrl))
      .subscribe({
        next: (signedUrl: string) => {
          this.imageSrc = signedUrl; // let the browser stream & decode
          this.imageLoaded.emit(signedUrl); // emit the URL, not a blob
        },
        error: (err) => console.error(err),
      });
  }

  public removeCurrentAvatar(): void {
    const loginName = this.entityService.user.loginName;
    this.UsersService.removeUserLogo(loginName, this.entityService.user.id).subscribe({
      next: () => {
        this.removeFile();
        this.lastUploadedFilename = null;
        this.lastUploadedFilePath = null;
      },
      error: (err) => {
        console.error('Error deleting avatar:', err);
      },
    });
  }

  public removeCurrentEntityLogo(): void {
    const loginName = this.entityService.user.loginName;
    this.entityService.removeEntityLogo(loginName).subscribe({
      next: () => {
        this.removeFile();
        this.lastUploadedFilename = null;
        this.lastUploadedFilePath = null;
      },
      error: (err) => {
        console.error('Error deleting avatar:', err);
      },
    });
  }

  // Public method to trigger upload from parent component
  public triggerUpload(): void {
    if (this.selectedFiles.length > 0 && !this.isUploading) {
      this.startUpload(new Event(''));
    }
  }

  // Public method to check if files are ready for upload
  public hasFilesToUpload(): boolean {
    return this.selectedFiles.length > 0 && !this.isUploading && !this.uploadCompleted;
  }

  // Public method to get selected files
  public getSelectedFiles(): File[] {
    return this.selectedFiles;
  }

  // Public method to get existing documents
  public getExistingDocuments(): { assetId: number; fileName: string; fileMimeType: string; assetUrl?: string }[] {
    return this.existingDocuments;
  }

  private emitPendingFiles(): void {
  if (this.fileTypeInS3 === 'COURSE_DOCUMENT' && !this.showFilesOnly) {
    const pendingFiles: PendingFileInfo[] = (this.uploadedFileInfo || []).map((file: any) => ({
      name: this.decodeFilename(file.name),
      size: file.size,
      mimeType: file.file?.type || file.fileMimeType
    }));
    this.pendingFilesChange.emit(pendingFiles);
  }
}

}
